import type { ReactNode } from 'react';
import styles from './Input.module.scss';

// ============================================
// FORM FIELD — Internal shared wrapper
// Used by TextInput, PhoneInput, Textarea
// NOT exported publicly from the Input barrel
// ============================================

interface FormFieldProps {
  /** Unique ID — links label (htmlFor) and error/helper (aria-describedby) */
  id: string;
  /** Label text displayed above the input */
  label?: string;
  /** Show required asterisk on the label */
  isRequired?: boolean;
  /** Help text below the input (hidden when error is present) */
  helperText?: string;
  /** Error message below the input (replaces helperText, triggers error styling) */
  error?: string;
  /** Disabled styling on the label */
  isDisabled?: boolean;
  /** Additional className on the outer .field wrapper */
  className?: string;
  /** The input element(s) to render inside the field */
  children: ReactNode;
}

function FormField({
  id,
  label,
  isRequired,
  helperText,
  error,
  className,
  children,
}: FormFieldProps) {
  const hasError = Boolean(error);

  const fieldClasses = [
    styles.field,
    hasError && styles.error,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={fieldClasses}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
          {isRequired && (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      {children}

      {hasError ? (
        <p id={`${id}-error`} className={styles.errorText} role="alert">
          <span className={styles.errorIcon} aria-hidden="true">
            <svg
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7 0a7 7 0 1 0 0 14A7 7 0 0 0 7 0Zm.75 4a.75.75 0 0 0-1.5 0v3a.75.75 0 0 0 1.5 0V4ZM7 9.5a.875.875 0 1 0 0 1.75.875.875 0 0 0 0-1.75Z"
                fill="currentColor"
              />
            </svg>
          </span>
          {error}
        </p>
      ) : helperText ? (
        <p id={`${id}-helper`} className={styles.helperText}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

export default FormField;
